import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import type { WorldStateResponse } from '@jetopolis/shared';
import { api, ApiError } from '../../app/api/client';
import { useAuth } from '../../app/stores/auth';
import { useWorld } from '../../app/stores/world';
import { WorldSocket } from '../../app/ws/client';
import { useIsMobile } from '../../hooks/useIsMobile';
import { MapView } from '../../map/MapView';
import { Panel, PanelHeader } from '../../ui/Panel';
import { Sheet } from '../../ui/Sheet';
import { Stat } from '../../ui/Stat';
import { TabBar } from '../../ui/TabBar';
import { TopBar } from '../../ui/TopBar';
import { Chip } from '../../ui/Chip';

function WorldCard() {
  const { t } = useTranslation();
  const { world, online } = useWorld();
  if (!world) return null;
  return (
    <Panel className="mt-2">
      <PanelHeader>{t('hq.panel.world')}</PanelHeader>
      <div className="grid grid-cols-2 gap-4 p-4">
        <Stat label={t('hq.world.date')} value={world.gameDate} tone="amber" />
        <Stat label={t('hq.world.type')} value={t(`lobby.type.${world.type}`)} />
        <Stat label={t('hq.world.region')} value={t(`lobby.region.${world.region}`)} />
        <Stat label={t('hq.world.players')} value={`${world.players}/${world.maxPlayers}`} />
        <Stat
          label={t('hq.world.years')}
          value={`${world.startYear}-${world.endYear}`}
          tone="dim"
        />
        <Stat label={t('hq.world.online')} value={String(online)} tone="cyan" />
      </div>
      <div className="flex gap-1 px-4 pb-4">
        <Chip tone={world.status === 'RUNNING' ? 'cyan' : 'red'}>
          {t(`hq.status.${world.status}`)}
        </Chip>
      </div>
    </Panel>
  );
}

export function Hq() {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const { hydrate, applyMessage, setConnection, setSocket, reset } = useWorld();

  useEffect(() => {
    if (id === undefined || !user) return;

    // Первичная загрузка через REST: сокет мог ещё не открыться, а показать
    // состояние мира нужно сразу.
    void (async () => {
      try {
        const state = await api.get<WorldStateResponse>(`/v1/worlds/${id}/state`);
        hydrate(state.world, state.online, state.nextTickAt);
      } catch (e) {
        console.error('[hq] состояние мира не загрузилось', e instanceof ApiError ? e.code : e);
      }
    })();

    const socket = new WorldSocket(id, i18n.language, {
      onMessage: applyMessage,
      onState: setConnection,
    });
    socket.connect();
    setSocket(socket);

    return () => {
      socket.close();
      setSocket(null);
      reset();
    };
  }, [id, user, i18n.language, hydrate, applyMessage, setConnection, setSocket, reset]);

  if (id === undefined) return null;

  return (
    <div className="flex h-dvh flex-col bg-ink">
      <TopBar />
      <div className="relative min-h-0 flex-1">
        <MapView />
        {!isMobile && (
          <aside className="absolute inset-y-0 end-0 w-[360px] overflow-y-auto border-s border-line bg-ink/95 p-3 backdrop-blur">
            <h1 className="px-1 font-[family-name:var(--font-display)] text-[15px] text-txt">
              {t('hq.title')}
            </h1>
            <WorldCard />
          </aside>
        )}
      </div>
      {isMobile && (
        <Sheet label={t('hq.sheet.handle')}>
          <h1 className="font-[family-name:var(--font-display)] text-[15px] text-txt">
            {t('hq.title')}
          </h1>
          <WorldCard />
        </Sheet>
      )}
      <TabBar worldId={id} />
    </div>
  );
}
